'use client';
import { useContext, useEffect, useState } from 'react';
import {
  MdAdd,
  MdCheck,
  MdPlaylistAdd,
  MdThumbDown,
  MdThumbDownOffAlt,
  MdThumbUp,
  MdThumbUpOffAlt
} from 'react-icons/md';
import { useQuery, useQueryClient, useMutation } from 'react-query';

import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';
import NextImage from 'next/image';

import ButtonWithLabel from '../../../../components/ButtonWithLabel';
import DownloadApp from '../../../../components/DownloadApp';
import Footer from '../../../../components/Footer';
import GenresList from '../../../../components/GenresList';
import ManageListsItemsModal from '../../../../components/ManageListsItemsModal';
import MovieOrTvRating from '../../../../components/MovieOrTvRating';
import Person from '../../../../components/Person';
import ResourceItem from '../../../../components/ResourceItem';
import WatchedButton from '../../../../components/WatchedButton';
import Wrapper from '../../../../components/Wrapper';
import AuthContext from '../../../../context/AuthContext';
import buildRouteUrl from '../../../../helpers/buildRouteUrl';
import useIntl from '../../../../hooks/useIntl';
import { Link } from '../../../../i18n/navigation';
import { Ad, init } from '../../../../lib/adsClient';
import api from '../../../../services/api';
import { Movie } from '../../../../types/types';
// import AdSense from '../AdSense';

const WatchProvidersList = dynamic(
  () => import('../../../../components/WatchProvidersList')
);

const CarouselSection = dynamic(
  () => import('../../../../components/CarouselSection')
);

interface PropTypes {
  initialData: Movie;
  isMobile?: boolean;
  region: string;
}

async function getMovie(
  movieId: string,
  params?: any,
  headers?: any
): Promise<{ data: Movie }> {
  const response = await api.get(`/v1/movies/${movieId}`, {
    params,
    headers
  });

  return response.data;
}

function ClientMovie({ initialData, isMobile }: PropTypes) {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const t = useTranslations();

  const [ad, setAd] = useState<Ad | null>(null);
  const auth = useContext(AuthContext);

  const [isManageListsItemsModalOpen, setIsManageListsItemsModalOpen] =
    useState(false);

  useEffect(() => {
    async function loadAd() {
      const adClient = await init();
      const loadedAd = await adClient.load();
      setAd(loadedAd);
    }
    loadAd();
  }, []);

  const movieId = initialData._id as string;

  const movieQuery = useQuery<{ data: Movie }>(
    `movie-${movieId}`,
    async () => {
      return await getMovie(
        movieId,
        {
          region: intl.locale
        },
        {
          'accept-language': intl.full_language
        }
      );
    },
    { initialData: { data: initialData } }
  );

  const { mutate: addToUserList } = useMutation(
    async ({ movieId }: { movieId: string }) => {
      const response = await api.post(`/v1/me/list`, {
        content_id: movieId,
        content_type: 'movie'
      });

      return response.data;
    },
    {
      onMutate: ({ movieId }) => {
        const previousMovie = queryClient.getQueryData(`movie-${movieId}`);

        queryClient.setQueryData(`movie-${movieId}`, (old: any) => {
          if (old) {
            return {
              meta: old.meta,
              data: {
                ...old.data,
                user_related: { ...old.data.user_related, is_on_userlist: true }
              }
            };
          }
        });

        return () =>
          queryClient.setQueryData(`movie-${movieId}`, previousMovie);
      },

      onError: (err, _, rollback: any) => rollback(),
      onSuccess: () => {
        queryClient.invalidateQueries('user-list');
        queryClient.invalidateQueries(`user-me-list-with-pagination`);
      }
    }
  );

  const { mutate: removeFromUserList } = useMutation(
    async ({ movieId }: { movieId: string }) => {
      const response = await api.delete(`/v1/me/list`, {
        data: {
          content_id: movieId,
          content_type: 'movie'
        }
      });

      return response.data;
    },
    {
      onMutate: ({ movieId }) => {
        const previousMovie = queryClient.getQueryData(`movie-${movieId}`);

        queryClient.setQueryData(`movie-${movieId}`, (old: any) => {
          if (old) {
            return {
              data: {
                ...old.data,
                user_related: {
                  ...old.data.user_related,
                  is_on_userlist: false
                }
              }
            };
          }
        });

        return () =>
          queryClient.setQueryData(`movie-${movieId}`, previousMovie);
      },

      onError: (err, _, rollback: any) => rollback(),
      onSuccess: () => {
        queryClient.invalidateQueries('user-list');
        queryClient.invalidateQueries(`user-me-list-with-pagination`);
      }
    }
  );

  const { mutate: rateMovie } = useMutation(
    async ({ movieId, rating }: { movieId: string; rating: number }) => {
      const response = await api.post(`/v1/movies/${movieId}/rating`, {
        rating
      });

      return response.data;
    },
    {
      onMutate: ({ movieId, rating }) => {
        const previousMovie = queryClient.getQueryData(`movie-${movieId}`);

        queryClient.setQueryData(`movie-${movieId}`, (old: any) => {
          if (old) {
            return {
              meta: old.meta,
              data: {
                ...old.data,
                user_related: { ...old.data.user_related, user_rating: rating }
              }
            };
          }
        });

        return () =>
          queryClient.setQueryData(`movie-${movieId}`, previousMovie);
      },
      onError: (err, _, rollback: any) => rollback()
    }
  );

  const { mutate: addToUserWatchedList } = useMutation(
    async ({ movieId }: { movieId: string }) => {
      const response = await api.post(`/v1/me/watchlist/movies`, {
        movie_id: movieId
      });

      return response.data;
    },
    {
      onMutate: ({ movieId }) => {
        const previousMovie = queryClient.getQueryData(`movie-${movieId}`);

        queryClient.setQueryData(`movie-${movieId}`, (old: any) => {
          return {
            meta: old.meta,
            data: {
              ...old.data,
              user_related: {
                ...old.data.user_related,
                is_watched: true,
                watch_count: 1
              }
            }
          };
        });

        return () =>
          queryClient.setQueryData(`movie-${movieId}`, previousMovie);
      },
      onSuccess: (_, { movieId }) => {
        queryClient.invalidateQueries(`movie-${movieId}`);
        queryClient.invalidateQueries(`users-${auth.userData?._id}`);
        queryClient.invalidateQueries('me-detailed-statistics');
        queryClient.invalidateQueries(
          `user-${auth.userData?._id}-watch-list-movies-with-pagination`
        );
      },
      onError: (err, _, rollback: any) => rollback()
    }
  );

  const { mutate: removeFromUserWatchedList } = useMutation(
    async ({ movieId }: { movieId: string }) => {
      const response = await api.delete(`/v1/me/watchlist/movies/${movieId}`);

      return response.data;
    },
    {
      onMutate: ({ movieId }) => {
        const previousMovie = queryClient.getQueryData(`movie-${movieId}`);

        queryClient.setQueryData(`movie-${movieId}`, (old: any) => {
          if (old) {
            return {
              data: {
                ...old.data,
                user_related: { ...old.data.user_related, is_watched: false }
              }
            };
          }
        });

        return () =>
          queryClient.setQueryData(`movie-${movieId}`, previousMovie);
      },

      onError: (err, _, rollback: any) => rollback(),
      onSuccess: () => {
        queryClient.invalidateQueries(`users-${auth.userData?._id}`);
        queryClient.invalidateQueries('me-detailed-statistics');
        queryClient.invalidateQueries(
          `user-${auth.userData?._id}-watch-list-movies-with-pagination`
        );
      }
    }
  );

  const { mutate: markMovieAsRewatched } = useMutation(
    async ({ movieId }: { movieId: string }) => {
      const response = await api.post(`/v1/me/watchlist/movies`, {
        movie_id: movieId,
        is_rewatch: true
      });

      return response.data;
    },
    {
      onMutate: () => {
        queryClient.setQueryData(`movie-${movieId}`, (old: any) => {
          if (old) {
            return {
              data: {
                ...old.data,
                user_related: {
                  ...old.data.user_related,
                  watch_count: old.data.user_related.watch_count + 1
                }
              }
            };
          }
        });
      },
      onSuccess: (_, { movieId }) => {
        queryClient.invalidateQueries(`movie-${movieId}`);
        queryClient.invalidateQueries(`users-${auth.userData?._id}`);
        queryClient.invalidateQueries('me-detailed-statistics');
      }
    }
  );

  const { mutate: resetMovieWatches } = useMutation(
    async ({ movieId }: { movieId: string }) => {
      const response = await api.delete(`/v1/me/watchlist/movies/${movieId}`, {
        data: {
          reset_watches: true
        }
      });

      return response.data;
    },
    {
      onMutate: ({ movieId }) => {
        const previousMovie = queryClient.getQueryData(`movie-${movieId}`);

        queryClient.setQueryData(`movie-${movieId}`, (old: any) => {
          if (old) {
            return {
              data: {
                ...old.data,
                user_related: {
                  ...old.data.user_related,
                  is_watched: false
                }
              }
            };
          }
        });

        queryClient.invalidateQueries(`users-${auth.userData?._id}`);

        return () =>
          queryClient.setQueryData(`movie-${movieId}`, previousMovie);
      },
      onSuccess: () => {
        queryClient.invalidateQueries(`users-${auth.userData?._id}`);
        queryClient.invalidateQueries('me-detailed-statistics');
        queryClient.invalidateQueries(
          `user-${auth.userData?._id}-watch-list-movies-with-pagination`
        );
      },
      onError: (err, _, rollback: any) => rollback()
    }
  );

  const { mutate: resetMovieRewatches } = useMutation(
    async ({ movieId }: { movieId: string }) => {
      const response = await api.delete(`/v1/me/watchlist/movies/${movieId}`, {
        data: {
          reset_rewatches: true
        }
      });

      return response.data;
    },
    {
      onMutate: () => {
        queryClient.setQueryData(`movie-${movieId}`, (old: any) => {
          if (old) {
            return {
              data: {
                ...old.data,
                user_related: {
                  ...old.data.user_related,
                  watch_count: 1
                }
              }
            };
          }
        });
      },
      onSuccess: (_, { movieId }) => {
        queryClient.invalidateQueries(`movie-${movieId}`);
        queryClient.invalidateQueries(`users-${auth.userData?._id}`);
        queryClient.invalidateQueries('me-detailed-statistics');
      }
    }
  );

  function handleAddOrRemoveWatchedList() {
    if (movieQuery.data?.data.user_related.is_watched) {
      removeFromUserWatchedList({ movieId });
    } else {
      addToUserWatchedList({ movieId });
    }
  }

  function handleAddOrRemoveUserList() {
    if (movieQuery.data?.data.user_related.is_on_userlist) {
      removeFromUserList({ movieId });
    } else {
      addToUserList({ movieId });
    }
  }

  function likeMovie() {
    if (movieQuery.data?.data.user_related.user_rating === 2) {
      rateMovie({ movieId, rating: 0 });
    } else {
      rateMovie({ movieId, rating: 2 });
    }
  }

  function dislikeMovie() {
    if (movieQuery.data?.data.user_related.user_rating === 1) {
      rateMovie({ movieId, rating: 0 });
    } else {
      rateMovie({ movieId, rating: 1 });
    }
  }

  const director = movieQuery.data?.data.credits.find(
    credit => credit.type === 'crew' && credit.job === 'Director'
  );

  const cast = movieQuery.data?.data.credits.filter(
    credit => credit.type === 'cast' && credit.avatar
  );

  const fullYear =
    new Date(String(movieQuery?.data?.data?.release_date)).getFullYear() ||
    null;

  return (
    <Wrapper>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org/',
            '@type': 'Movie',
            name: initialData.title,
            description: initialData.overview
          })
        }}
      />
      {!auth.isLoading && auth.isLoggedIn && movieQuery.data?.data._id && (
        <ManageListsItemsModal
          isOpen={isManageListsItemsModalOpen}
          onClose={() => setIsManageListsItemsModalOpen(false)}
          data={{ contentId: movieQuery.data?.data._id, contentType: 'movie' }}
        />
      )}

      <div className="flex flex-col min-h-[100vh]">
        {movieQuery.isSuccess && (
          <div className="flex flex-col w-full">
            <div
              className={`
                flex
                flex-col
                md:flex-row
                px-4 md:px-10
                py-4 md:py-6
                relative
              `}
              style={{
                backgroundImage: `linear-gradient(rgba(20, 20, 20, 0.958), rgba(20, 20, 20, 0.958)), url(https://www.themoviedb.org/t/p/w1920_and_h800_multi_faces${movieQuery.data?.data?.wallpaper?.file_path})`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover'
              }}
            >
              {isMobile && (
                <div className="flex md:hidden">
                  <NextImage
                    className="rounded-md bg-gray-400 mr-4 z-[2] min-w-[70px] w-[70px] h-[105px]"
                    src={`https://cdn-proxy.trakio.app/t/p/w500${movieQuery.data?.data?.poster?.file_path}`}
                    alt={t(`images_alts.movie_poster`, {
                      title: movieQuery.data.data.title
                    })}
                    width={70}
                    height={105}
                    unoptimized
                  />

                  <h1 className="text-white text-2xl font-bold">
                    {movieQuery.data?.data.title}
                    {fullYear ? ' - ' + fullYear : ''}
                  </h1>
                </div>
              )}
              {typeof isMobile === 'undefined' && (
                <div className="flex md:hidden">
                  <NextImage
                    className="rounded-md bg-gray-400 mr-4 z-[2] min-w-[70px] w-[70px] h-[105px]"
                    width={70}
                    height={105}
                    src={`https://cdn-proxy.trakio.app/t/p/w500${movieQuery.data?.data?.poster?.file_path}`}
                    alt={t(`images_alts.movie_poster`, {
                      title: movieQuery.data.data.title
                    })}
                    unoptimized
                  />

                  <h1 className="text-white text-2xl font-bold">
                    {movieQuery.data?.data.title}
                    {fullYear ? ' - ' + fullYear : ''}
                  </h1>
                </div>
              )}
              {!isMobile && (
                <NextImage
                  className="rounded-md bg-gray-400 mr-4 z-[2] hidden md:block min-w-[360px] w-[360px] h-[540px]"
                  width={360}
                  height={540}
                  src={`https://cdn-proxy.trakio.app/t/p/w500${movieQuery.data?.data?.poster?.file_path}`}
                  alt={t(`images_alts.movie_poster`, {
                    title: movieQuery.data.data.title
                  })}
                  unoptimized
                />
              )}
              <div className="flex flex-col w-full z-[2]">
                {!isMobile && (
                  <h1 className="hidden md:block text-white text-3xl font-bold">
                    {movieQuery.data?.data.title}
                    {fullYear ? ' - ' + fullYear : ''}
                  </h1>
                )}
                <p className="w-full lg:w-[60%] text-white text-md line-clamp-6 mt-1">
                  {movieQuery.data?.data.overview}
                </p>

                <div className="flex text-white mt-2">
                  {!!movieQuery?.data?.data?.runtime && (
                    <div className="flex mr-2 h-[25px] items-center justify-center whitespace-nowrap">
                      <span className="text-base font-bold">
                        {movieQuery?.data?.data?.runtime + 'min'}
                      </span>
                    </div>
                  )}

                  {!!fullYear && (
                    <div className="flex mr-2 h-[25px] items-center justify-center whitespace-nowrap">
                      <span className="text-base font-bold">{fullYear}</span>
                    </div>
                  )}

                  {!!movieQuery?.data?.data?.certification && (
                    <div className="flex mr-2 h-[25px] items-center justify-center whitespace-nowrap">
                      <span className="text-base font-bold">
                        {movieQuery?.data?.data?.certification || 'NA'}
                      </span>
                    </div>
                  )}

                  {!!movieQuery?.data?.data?.status && (
                    <div className="flex mr-2 h-[25px] items-center justify-center whitespace-nowrap">
                      <span className="text-base font-bold">
                        {t(`movie.status.${movieQuery?.data?.data?.status}`)}
                      </span>
                    </div>
                  )}
                </div>

                <GenresList
                  data={movieQuery.data?.data.genres || []}
                  from_page="movie"
                />

                <MovieOrTvRating
                  data={{
                    internal_rating: movieQuery.data?.data.average_rating || 0,
                    imdb_rating: movieQuery.data?.data.imdb_rating
                  }}
                />

                {!auth.isLoading &&
                  auth.isLoggedIn &&
                  movieQuery.data?.data.user_related && (
                    <div className="flex flex-row mt-8">
                      <ButtonWithLabel
                        className="mr-3"
                        onClick={likeMovie}
                        label={t('common.like')}
                        icon={
                          movieQuery.data?.data.user_related.user_rating ===
                          2 ? (
                            <MdThumbUp className="text-white" size={26} />
                          ) : (
                            <MdThumbUpOffAlt className="text-white" size={26} />
                          )
                        }
                      />

                      <ButtonWithLabel
                        className="mr-3"
                        onClick={dislikeMovie}
                        label={t('common.dislike')}
                        icon={
                          movieQuery.data?.data.user_related.user_rating ===
                          1 ? (
                            <MdThumbDown className="text-white" size={26} />
                          ) : (
                            <MdThumbDownOffAlt
                              className="text-white"
                              size={26}
                            />
                          )
                        }
                      />

                      <ButtonWithLabel
                        className="mr-3"
                        onClick={handleAddOrRemoveUserList}
                        label={t('movie.my_list')}
                        icon={
                          movieQuery.data?.data.user_related.is_on_userlist ? (
                            <MdCheck className="text-white" size={26} />
                          ) : (
                            <MdAdd className="text-white" size={26} />
                          )
                        }
                      />

                      <ButtonWithLabel
                        className="mr-3"
                        onClick={() => setIsManageListsItemsModalOpen(true)}
                        label={t('movie.list')}
                        icon={
                          <MdPlaylistAdd className="text-white" size={26} />
                        }
                      />
                    </div>
                  )}

                {!auth.isLoading &&
                  auth.isLoggedIn &&
                  movieQuery.data?.data.user_related &&
                  !auth.isLoadingSubscription && (
                    <div className="flex flex-row mt-6">
                      {auth.userSubscription?.status !== 'active' && (
                        <WatchedButton
                          btnType="text"
                          isWatched={
                            movieQuery.data?.data.user_related.is_watched
                          }
                          onClick={handleAddOrRemoveWatchedList}
                        />
                      )}

                      {auth.userSubscription?.status === 'active' &&
                        !movieQuery.data.data.user_related.is_watched && (
                          <WatchedButton
                            btnType="text"
                            isWatched={
                              movieQuery.data?.data.user_related.is_watched
                            }
                            onClick={handleAddOrRemoveWatchedList}
                          />
                        )}

                      {auth.userSubscription?.status === 'active' &&
                        movieQuery.data?.data.user_related.is_watched && (
                          <WatchedButton
                            watchCount={
                              movieQuery.data.data.user_related.watch_count
                            }
                            btnType="text"
                            withOptions
                            isWatched={
                              movieQuery.data?.data.user_related.is_watched
                            }
                            onRewatch={() =>
                              markMovieAsRewatched({
                                movieId
                              })
                            }
                            onResetWatches={() =>
                              resetMovieWatches({
                                movieId
                              })
                            }
                            onResetRewatches={() =>
                              resetMovieRewatches({
                                movieId
                              })
                            }
                          />
                        )}
                    </div>
                  )}
              </div>
            </div>

            <div
              className="flex flex-row justify-between items-start flex-wrap px-4 md:px-10
                py-4 md:py-6"
            >
              <div className="w-full lg:w-[100%]">
                <WatchProvidersList
                  id={`movies-${movieId}-watch-providers`}
                  contentTitle={movieQuery.data.data.title}
                  data={movieQuery.data?.data.watch_providers || []}
                  className="mb-6"
                />

                {(cast?.length || 0) > 0 && (
                  <CarouselSection
                    id={`movies-${movieId}-cast`}
                    title={t('movie.sections.cast')}
                    type="person"
                    totalItems={cast?.length || 0}
                    className="mb-6"
                  >
                    {cast?.map(item => (
                      <div key={item._id} className="embla__slide">
                        <Person key={item._id} data={item} />
                      </div>
                    ))}
                  </CarouselSection>
                )}

                {(movieQuery.data?.data?.related_movies?.length || 0) > 0 && (
                  <CarouselSection
                    id={`movies-${movieId}-related_movies`}
                    title={t('movie.sections.you_may_also_like')}
                    type="resource"
                    totalItems={movieQuery.data.data.related_movies.length || 0}
                    className="mt-12"
                  >
                    {movieQuery.data.data.related_movies.map(item => {
                      return (
                        <Link
                          key={item._id}
                          href={buildRouteUrl(`/movie/${item._id}`)}
                        >
                          <div className="embla__slide">
                            <ResourceItem
                              className="mr-2"
                              data={{ ...item, type: 'movie' }}
                            />
                          </div>
                        </Link>
                      );
                    })}
                  </CarouselSection>
                )}

                <div className="w-full mt-20">
                  <h2 className={`text-white text-2xl font-normal`}>
                    {t('movie.additional_data.title', {
                      content_title: movieQuery.data.data.title
                    })}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                    <div className="flex flex-col w-full lg-[w-1/2]">
                      {!!movieQuery.data.data.overview && (
                        <h3 className="text-white text-lg font-bold mt-2">
                          {t('movie.additional_data.overview', {
                            content_title: movieQuery.data.data.title
                          })}
                        </h3>
                      )}

                      {!!movieQuery.data.data.overview && (
                        <p className="text-[#a3a3a3] mt-2">
                          {movieQuery.data.data.overview}
                        </p>
                      )}

                      {movieQuery.data.data.certification && (
                        <div className="mt-2">
                          <strong className="text-white">
                            {t('movie.additional_data.certification')}:
                          </strong>{' '}
                          <span className="text-[#a3a3a3]">
                            {movieQuery.data.data.certification}
                          </span>
                        </div>
                      )}

                      {!!movieQuery.data?.data.runtime && (
                        <div className="mt-2">
                          <strong className="text-white">
                            {t('movie.additional_data.runtime')}:
                          </strong>{' '}
                          <span className="text-[#a3a3a3]">
                            {movieQuery?.data?.data?.runtime + 'min'}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col w-full lg-[w-1/2]">
                      <h3 className="text-white text-lg font-bold">
                        {t('movie.additional_data.extra_details')}
                      </h3>

                      {!!movieQuery.data.data.original_title && (
                        <div className="mt-2">
                          <strong className="text-white">
                            {t('movie.additional_data.original_title')}:
                          </strong>{' '}
                          <span className="text-[#a3a3a3]">
                            {movieQuery.data?.data.original_title}
                          </span>
                        </div>
                      )}

                      {!!movieQuery.data.data.original_language &&
                        movieQuery.data.data.original_language !== 'xx' && (
                          <div className="mt-2">
                            <strong className="text-white">
                              {t('movie.additional_data.original_language')}:
                            </strong>{' '}
                            <span className="text-[#a3a3a3]">
                              {t(
                                `iso_639_1.${movieQuery.data?.data.original_language}`
                              )}
                            </span>
                          </div>
                        )}

                      {!!movieQuery.data?.data.budget && (
                        <div className="mt-2">
                          <strong className="text-white">
                            {t('movie.additional_data.budget')}:
                          </strong>{' '}
                          <span className="text-[#a3a3a3]">
                            {movieQuery.data?.data.budget.toLocaleString(
                              'us-US',
                              {
                                style: 'currency',
                                currency: 'USD'
                              }
                            )}
                          </span>
                        </div>
                      )}

                      {!!movieQuery.data?.data.revenue && (
                        <div className="mt-2">
                          <strong className="text-white">
                            {t('movie.additional_data.revenue')}:
                          </strong>{' '}
                          <span className="text-[#a3a3a3]">
                            {movieQuery.data?.data.revenue.toLocaleString(
                              'us-US',
                              {
                                style: 'currency',
                                currency: 'USD'
                              }
                            )}
                          </span>
                        </div>
                      )}

                      {director && (
                        <div className="mt-2">
                          <strong className="text-white">
                            {t('movie.additional_data.directed_by')}:
                          </strong>{' '}
                          <Link
                            className="text-[#a3a3a3]"
                            href={
                              initialData
                                ? buildRouteUrl(`/person/c/${director._id}`)
                                : buildRouteUrl(`/person/${director._id}`)
                            }
                          >
                            {director?.name}
                          </Link>
                        </div>
                      )}

                      {movieQuery.data?.data.production_companies.length >
                        0 && (
                        <div className="mt-2">
                          <span className="font-bold text-white">
                            {t('movie.additional_data.production_companies')}
                          </span>
                          <div className="flex flex-row flex-wrap">
                            {movieQuery.data?.data.production_companies.map(
                              (productionCompany, index) => {
                                const isLastItem =
                                  movieQuery.data.data.production_companies
                                    .length -
                                    1 ===
                                  index;

                                return (
                                  <span
                                    key={productionCompany.name}
                                    className="text-[#a3a3a3]"
                                  >
                                    {productionCompany.name}
                                    {movieQuery.data.data.production_companies
                                      .length > 1 &&
                                      !isLastItem &&
                                      ',\u00A0'}
                                  </span>
                                );
                              }
                            )}
                          </div>
                        </div>
                      )}

                      <div className="mt-2">
                        {movieQuery.data?.data.watch_providers
                          .filter(
                            (v, i, a) => a.findIndex(t => t.url === v.url) === i
                          )
                          .filter(
                            watchProviderOffer =>
                              !(
                                watchProviderOffer.provider.slug ===
                                  'unofficial' ||
                                watchProviderOffer.provider.slug === 'theater'
                              )
                          ).length > 0 ? (
                          <>
                            <strong className="text-white">
                              {t('movie.additional_data.where_to_watch')}
                            </strong>
                            <p className="text-[#a3a3a3]">
                              {t('movie.additional_data.where_to_watch_list', {
                                content_title: movieQuery.data.data.title
                              })}{' '}
                              {movieQuery.data.data.watch_providers
                                .filter(
                                  (v, i, a) =>
                                    a.findIndex(t => t.url === v.url) === i
                                )
                                .filter(
                                  watchProviderOffer =>
                                    !(
                                      watchProviderOffer.provider.slug ===
                                        'unofficial' ||
                                      watchProviderOffer.provider.slug ===
                                        'theater'
                                    )
                                )
                                .map((watchProvider, index, arr) => {
                                  const isLastItem = arr.length - 1 === index;

                                  return (
                                    <a
                                      key={watchProvider.provider._id}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="hover:opacity-[0.5]"
                                      href={watchProvider.url}
                                    >
                                      <strong>
                                        {watchProvider.provider.name}
                                        {movieQuery.data.data.watch_providers
                                          .length > 1 &&
                                          !isLastItem &&
                                          ', '}
                                      </strong>
                                    </a>
                                  );
                                })}
                            </p>
                          </>
                        ) : (
                          <div>
                            <strong className="text-white">
                              {t('movie.additional_data.where_to_watch')}
                            </strong>
                            <p className="text-[#a3a3a3]">
                              {t('movie.additional_data.no_where_to_watch', {
                                title: movieQuery.data.data.title
                              })}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <DownloadApp />
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </Wrapper>
  );
}

export default ClientMovie;
